import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <h1>Terms &amp; Conditions</h1>
          <p className="legal-subtitle">
            Please read these Terms &amp; Conditions carefully before using the E-Invitation service
            operated by <strong>Logica Technology</strong>.
          </p>
          <p className="legal-effective">Effective Date: April 15, 2026 &nbsp;|&nbsp; Last Updated: April 15, 2026</p>
        </div>

        <div className="legal-body">

          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By submitting a booking request or using any part of the E-Invitation service, you
              ("the Client") agree to be bound by these Terms &amp; Conditions. If you do not agree,
              please do not proceed with the booking.
            </p>
            <p>
              These Terms constitute a binding agreement between the Client and <strong>Logica Technology</strong>,
              the provider of the E-Invitation service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Services Offered</h2>
            <p>Logica Technology provides digital e-invitation design services, including:</p>
            <ul>
              <li><strong>Standard E-Invitation Package</strong> — Personalized digital invitation website starting at ₱800</li>
              <li><strong>Optional Add-ons</strong> — Rush handling, additional photos, printable files, photo album, and other special requests</li>
            </ul>
            <p>
              Final pricing, add-ons, and delivery schedule are confirmed through direct communication via Messenger after the
              booking form is submitted. Prices are in <strong>Philippine Peso (₱)</strong>.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Booking Process</h2>
            <ol>
              <li>Client submits a booking request through the website form.</li>
              <li>Logica Technology contacts the Client via Facebook Messenger within 24 hours to confirm the order details.</li>
              <li>Final price is agreed upon during the consultation.</li>
              <li>A <strong>50% downpayment</strong> is required before design work begins. Payment details are sent via Messenger.</li>
              <li>Design is created and shared with the Client for review.</li>
              <li>Client may request revisions (subject to revision limits per Section 5).</li>
              <li>Upon Client approval, the remaining <strong>50% balance</strong> must be paid in full.</li>
              <li>Final e-invitation file is delivered after full payment is confirmed.</li>
            </ol>
          </section>

          <section className="legal-section">
            <h2>4. Payment Terms</h2>
            <ul>
              <li><strong>Downpayment:</strong> 50% of the agreed price is due upon order confirmation to commence design work.</li>
              <li><strong>Balance:</strong> The remaining 50% is due once the design is approved by the Client.</li>
              <li>Payment is made via channels communicated through Facebook Messenger (e.g., GCash, bank transfer).</li>
              <li>Logica Technology reserves the right to withhold delivery of the final file until full payment is received.</li>
              <li>All prices are in Philippine Peso (₱) and are inclusive of applicable fees unless otherwise stated.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Revisions</h2>
            <ul>
              <li><strong>Standard package:</strong> Revision scope is confirmed during booking based on the submitted event details.</li>
              <li><strong>Unlimited revisions:</strong> Available as an optional add-on when the Client needs extended refinement.</li>
              <li>Additional revisions beyond the included limit may incur extra charges, which will be communicated to the Client before proceeding.</li>
              <li>Revisions must be requested before final payment and delivery. No revisions are accepted after the final file has been delivered.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Turnaround Time</h2>
            <ul>
              <li><strong>Standard package:</strong> Delivered within <strong>2-3 days</strong> after downpayment and complete event details are received.</li>
              <li><strong>Rush handling:</strong> May be available as an add-on depending on schedule and scope.</li>
              <li>Turnaround times may be affected by incomplete information, delayed communication, or revision requests from the Client.</li>
              <li>Rush orders may be accommodated subject to availability and may incur additional fees.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Cancellation and Refund Policy</h2>
            <ul>
              <li>Cancellations made <strong>before design work has started</strong>: Full refund of downpayment.</li>
              <li>Cancellations made <strong>after design work has started</strong>: Downpayment is non-refundable as it covers labor and design time already rendered.</li>
              <li>If Logica Technology is unable to deliver the service for reasons within our control, a full refund will be issued.</li>
              <li>Refund requests must be submitted in writing via email at <a href="mailto:sales@logicatechnology.com">sales@logicatechnology.com</a> or through Facebook Messenger.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Client Responsibilities</h2>
            <p>The Client agrees to:</p>
            <ul>
              <li>Provide accurate and complete event information required for the design.</li>
              <li>Respond to Logica Technology's messages in a timely manner.</li>
              <li>Review and approve the design within <strong>7 calendar days</strong> of receiving the draft. Failure to respond within this period may be treated as approval.</li>
              <li>Ensure that any reference materials, photos, or content submitted do not infringe third-party intellectual property rights.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Intellectual Property</h2>
            <ul>
              <li>Upon receipt of full payment, the Client is granted a <strong>non-exclusive, personal-use license</strong> to use the delivered e-invitation for their specific event.</li>
              <li>Logica Technology retains the right to display the completed design in its portfolio, social media, and promotional materials, unless the Client explicitly requests otherwise.</li>
              <li>The Client may not resell, redistribute, or use the design for commercial purposes without written consent from Logica Technology.</li>
              <li>Sample design directions, reusable layouts, and underlying implementation remain the intellectual property of Logica Technology. Customizations made for the Client are licensed for personal event use only.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>10. Limitation of Liability</h2>
            <p>
              Logica Technology shall not be held liable for:
            </p>
            <ul>
              <li>Errors in the design due to incorrect information provided by the Client</li>
              <li>Technical issues outside of our control (e.g., internet outages, third-party platform downtime)</li>
              <li>Any indirect, incidental, or consequential damages arising from the use of the e-invitation</li>
            </ul>
            <p>
              Our maximum liability to the Client shall not exceed the total amount paid for the specific order in question.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Privacy</h2>
            <p>
              Your personal information is processed in accordance with our{' '}
              <Link to="/privacy">Privacy Notice</Link>, which complies with the
              Data Privacy Act of 2012 (RA 10173) of the Philippines.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Governing Law and Jurisdiction</h2>
            <p>
              These Terms &amp; Conditions are governed by the laws of the <strong>Republic of the Philippines</strong>.
              Any dispute arising from or related to these Terms shall be submitted to the proper courts
              having jurisdiction in the Philippines, without prejudice to alternative dispute resolution
              mechanisms under Philippine law.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Amendments</h2>
            <p>
              Logica Technology reserves the right to update or modify these Terms &amp; Conditions at
              any time. Changes will be posted on this page with an updated effective date. Continued
              use of our services after changes are posted constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Contact Us</h2>
            <p>For questions or concerns regarding these Terms, please contact us:</p>
            <div className="legal-contact-box">
              <p><strong>Logica Technology</strong></p>
              <p>Email: <a href="mailto:sales@logicatechnology.com">sales@logicatechnology.com</a></p>
              <p>Website: <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer">logicatechnology.com</a></p>
              <p>Facebook: <a href="https://www.facebook.com/profile.php?id=61585468153192" target="_blank" rel="noopener noreferrer">Logica Technology</a></p>
            </div>
          </section>

        </div>

        <div className="legal-footer">
          <Link to="/privacy" className="legal-back-link">← Privacy Notice</Link>
          <Link to="/" className="legal-back-link">Back to Home →</Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
